/**
 * External Imports
 */
import {
  FC,
  Fragment,
  MouseEvent,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef
} from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Nexus Imports
 */
import {
  NeuAvatar,
  NeuButton,
  NeuCheckbox,
  NeuImage,
  NeuInput,
  NeuRadio
} from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import RoundingTypeDropdown from '../../customDropdowns/roundingTypeDropdown';
import { BlueChip } from '../../shared/chips/blueChip';
import { Dropdown } from '../../shared/dropdowns';
import { DeleteBtn, MarkCompleteBtn } from '../../shared/buttons';
import CategorySelect from '../reusableModalPieces/categorySelect/CategorySelect';
import EmployeeSearch from '../reusableModalPieces/employeeSearch';
import IssueSubHeader from '../reusableModalPieces/subHeader';
import PatientSearch from '../reusableModalPieces/patientSearch';
import SearchView from '../reusableModalPieces/searchView';

import { RootState } from '../../../redux/store';
import {
  openModal,
  setModalType,
  setModalAction,
  clearPatientSearch,
  getRound,
  setPendingIssue,
  clearRoundState,
  getStoplightById,
  clearStoplight,
  postNewTask,
  getCategoriesByType,
  getSCLocations,
  getGELocations,
  verifyControlNumber,
  clearControlNumberResponse,
  getTaskDiscussion,
  postTaskComment,
  addTaskToSharingQ,
  saveTaskData,
  clearTaskData,
  saveTaskModalState,
  clearTaskModalState,
  setToastType,
  ISaveTaskModalStatePayload
} from '../../../redux/actions';
import {
  useLazyGetEmployeesByIdsQuery,
  useLazyGetEmployeesBySearchQuery
} from '../../../redux/apis/Employees.api';

import { handleModal } from '../../../services/ModalPortal/utils';
import useToast from '../../../services/Toast';
import { useUserRolesRef } from '../../../services/UserRoles';
import { logAdHocClick, logTaskClick } from '../../../utils/analyticsHelpers';
import { useDebounceValue } from '../../../utils/debouncers';
import {
  compareObjByStringKey,
  createAnyId,
  date,
  formatPhoneNumber,
  formatRoundingType,
  reverseName,
  text,
  toTitleCase
} from '../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  Category,
  Comment,
  Employee,
  GELocation,
  ITaskShareObj,
  ITempTaskWithNewStoplight,
  Issue,
  Patient,
  SCLocation,
  Stoplight,
  Task,
  TaskPost,
  Unit
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import {
  AddIssueCard,
  BtnSpinner,
  CardHeader,
  CardMainHeader,
  CardTitle,
  InputHeading,
  LabelContentBold,
  LabelHeading,
  ModalCardContent,
  ModalTextArea,
  ModalFooter,
  ServiceCentralSection,
  StyledBoldHeading,
  StyledBoldHeadingCompleted,
  StyledCircleDoneIcon,
  StyledDivider,
  StyledEditHeaderCol,
  StyledEditHeaderRow,
  StyledRightAlignButton,
  TitleSpan,
  TitleIcon,
  UnknownControlContainer,
  ViewStoplightsClickableLabel
} from './AddIssueModal.styles';
import { ChipWrapper } from '../feedbackModal/FeedbackModal.styles';
import { StyledBoldLabel } from '../../shared/table/Table.styles';

/**
 * Static File Imports
 */
import serviceCentralLogoImg from '../../../assets/images/service_central_icon.png';

/**
 * Constant Imports
 */
import issueTypes from '../../../constants/validRoundingTypesWithTasks.json';
import itAndSCategories from '../../../constants/it&sCategories.json';
import serviceCentralCategories from '../../../constants/serviceCentralCategories.json';

interface ITaskModalProps {
  attachedStoplight?: Stoplight | null;
  authorizedUser: AuthUserProps;
  categories: Category[];
  controlNumberResponse: Nullable<string>;
  edit?: boolean;
  geLocations: GELocation[];
  isLoadingControlNumber: boolean;
  isRoundStarted: boolean;
  issueData: Task;
  loadingSaveTask: boolean;
  modalAction: string;
  modalType: string;
  scLocations: SCLocation[];
  stoplight: Stoplight | null;
  tempModalState?: ISaveTaskModalStatePayload;
  tempTaskObject?: ITempTaskWithNewStoplight;
  tempShareWithEmailObject?: ITaskShareObj;
  units: Unit[];
  userSection: string;
}

type Nullable<T> = T | null;

type RequiredItemTypes = {
  [key: string]: string | string[] | boolean | undefined;
};

const requiredItems: RequiredItemTypes = {
  category: '',
  description: ''
};

const AddIssueModal: FC<ITaskModalProps> = ({
  attachedStoplight,
  authorizedUser,
  categories,
  controlNumberResponse,
  edit,
  geLocations = [],
  isRoundStarted,
  isLoadingControlNumber,
  issueData,
  loadingSaveTask,
  modalAction,
  modalType,
  scLocations = [],
  stoplight,
  tempModalState,
  tempTaskObject,
  tempShareWithEmailObject,
  units,
  userSection
}) => {
  const dispatch = useDispatch();

  const { isIlobRounder } = useUserRolesRef();

  const [requiredFields, setRequiredFields] = useState(requiredItems);
  const [showButton, setShowButton] = useState<boolean>(false);
  const [view, setView] = useState<'info' | 'search'>('info');
  const [impactPatient, setImpactPatient] = useState<boolean>(false);
  const [typeOfItemNeed, setTypeOfItemNeed] = useState<string>('');
  const [taskObject, setTaskObject] = useState<TaskPost>(
    !edit
      ? {
          taskId: createAnyId('task', authorizedUser.facilityId, isIlobRounder),
          taskType: 'issue',
          category: tempTaskObject ? tempTaskObject.category : '',
          subCategory: tempTaskObject ? tempTaskObject.subCategory : '',
          completed: false,
          taskDate: date.now(),
          description: tempTaskObject ? tempTaskObject.description : '',
          facilityId: isIlobRounder ? '' : authorizedUser.facilityId,
          roundingType: issueTypes.includes(userSection) ? userSection : '',
          sharedWith: tempTaskObject?.sharedWith
            ? tempTaskObject.sharedWith
            : [],
          userId: authorizedUser.hcaid,
          userFullName: `${authorizedUser.firstName} ${authorizedUser.lastName}`,
          status: '',
          _source: 'web'
        }
      : {
          ...issueData,
          updatedBy: authorizedUser.hcaid,
          sharedWith: issueData?.sharedWith ? [...issueData.sharedWith] : []
        }
  );
  const [haveControlNumber, setHaveControlNumber] = useState<boolean>(true);

  const [shareObject, setShareObject] = useState<ITaskShareObj>({
    taskId: tempShareWithEmailObject?.taskId || '',
    userName: tempShareWithEmailObject?.userName || '',
    email: tempShareWithEmailObject?.email || []
  });
  const [comment, setComment] = useState('');
  const [activeSearch, setActiveSearch] = useState<string>('taskCategory');
  // TODO: These need the is prefix
  const [openRoundingType, setOpenRoundingType] = useState<boolean>(false);
  const [openCat, setOpenCat] = useState<boolean>(false);
  const [openSubcat, setOpenSubcat] = useState<boolean>(false);
  const [openUnit, setOpenUnit] = useState<boolean>(false);
  const [subCategoryPopState, setSubCategoryPopState] =
    useState<boolean>(false);
  const [scLocationPopState, setSCLocationPopState] = useState<boolean>(false);
  const [sharedPopState, setSharedPopState] = useState<boolean>(false);
  const [subCategorySearchTerm, setSubCategorySearchTerm] = useState('');
  const [scLocationSearchTerm, setSCLocationSearchTerm] = useState('');
  const [sharedSearchTerm, setSharedSearchTerm] = useState('');
  const [sharedWithNoEmail, setSharedWithNoEmail] = useState<boolean>(false);

  const modalContentRef = useRef<HTMLNeuCardElement>(null);

  // debouncing search term
  const dbSharedSearchTerm = useDebounceValue(sharedSearchTerm, 250);

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const {
    isCNEdRounder,
    isCSCRounder,
    isEnterpriseAdmin,
    isNonSupportRounder
  } = useUserRolesRef();

  const { toast } = useToast();

  const { logTrackingEvent } = useAnalyticsApi();

  const [
    getEmployeesByIds,
    {
      data: sharedEmployeesData
      // isError: isGetEmployeesByIdsError,
      // isFetching: isGetEmployeesByIdsFetching,
      // isLoading: isGetEmployeesByIdsLoading
    }
  ] = useLazyGetEmployeesByIdsQuery();
  const [
    getEmployeesBySearch,
    {
      data: employeesBySearchData
      // isError: isGetEmployeesBySearchError,
      // isFetching: isGetEmployeesBySearchFetching,
      // isLoading: isGetEmployeesBySearchLoading
    }
  ] = useLazyGetEmployeesBySearchQuery();

  const deepLinkTaskView = pathname.includes('task/complete');

  const categoryNames = useMemo(
    () =>
      [...categories].sort(compareObjByStringKey('name')).map(cat => cat.name),
    [categories]
  );

  const subcategoryNames = useMemo(
    () =>
      [
        ...(categories.find((cat: Category) => taskObject.category === cat.name)
          ?.subCategories || [])
      ].sort(),
    [categories, taskObject.category]
  );

  const issueRedirectPath = useMemo(() => {
    switch (taskObject.roundingType) {
      case 'patient':
        return '/patients/issues';
      case 'employee':
        return '/employees/issues';
      default:
        return `/support/${taskObject.roundingType}/issues`;
    }
  }, [taskObject.roundingType]);

  const changeIssueOrigin = (type: string) => {
    setTaskObject({
      ...taskObject,
      roundingType: type,
      category: ''
    });
  };

  const handleSharedSearching = (e: any) => {
    const target = e.target as HTMLInputElement;
    setSharedSearchTerm(target.value);
  };

  const handleInputs = (e: any) => {
    const target = e.target as HTMLInputElement;
    const { name, value } = target;

    let tempFields = {};
    if (
      name === 'callback' &&
      ((value && value.length === 10) || value.length === 14)
    ) {
      const callbackNumberNoCharacters = value.replace(/[^\w\s]/gi, '');
      tempFields = {
        ...tempFields,
        u_best_contact_info: formatPhoneNumber(callbackNumberNoCharacters)
      };
      setTaskObject({
        ...taskObject,
        ...tempFields,
        [name]: formatPhoneNumber(callbackNumberNoCharacters)
      });
    } else if (name === 'category') {
      dispatch(clearControlNumberResponse());
      setTaskObject({
        ...taskObject,
        ...tempFields,
        subCategory: '',
        [name]: value
      });
    } else {
      setTaskObject({ ...taskObject, ...tempFields, [name]: value });
    }
    // if user deselects unknown control number check box, then reset unknown inputs if any
    if (name === 'u_control_number' && !haveControlNumber) {
      setHaveControlNumber(true);
      const controlTaskObject = taskObject;
      if (
        taskObject.u_reason_for_no_control_number ||
        taskObject.u_issue_location
      ) {
        delete controlTaskObject.u_reason_for_no_control_number;
        delete controlTaskObject.u_issue_location;
        setTaskObject(controlTaskObject);
      }
    }

    if (Object.keys(requiredFields).includes(name)) {
      // For validation
      setRequiredFields(
        name === 'callback' && (value.length === 10 || 14)
          ? { ...requiredFields, [name]: value, u_best_contact_info: value }
          : { ...requiredFields, [name]: value }
      );
    }
  };

  const handleIsImpactPatient = (isPatientImpact: string) => {
    if (isPatientImpact === 'yes') {
      setImpactPatient(true);
      setTaskObject({
        ...taskObject,
        patientImpact: true
      });
    } else {
      setImpactPatient(false);
      setTaskObject({
        ...taskObject,
        patientImpact: false
      });
    }
  };

  const handleSupplyChainItemType = (typeOfItemNeeded: string) => {
    setTypeOfItemNeed(typeOfItemNeeded);
    setTaskObject({ ...taskObject, subCategory: typeOfItemNeeded });
    if (
      !typeOfItemNeeded.includes('Non-Routine') &&
      taskObject.u_non_routine_justification
    ) {
      const copyTaskObj = { ...taskObject };
      delete copyTaskObj.u_non_routine_justification;
      setTaskObject(copyTaskObj);
    }
  };

  const handleDropdownChange = useCallback(
    (value: string, name: string) => {
      if (name === 'category') {
        dispatch(clearControlNumberResponse());
        setTaskObject({
          ...taskObject,
          [name]: value,
          subCategory: '',
          ec_loc: '',
          u_ge_loc: ''
        });
      } else {
        setTaskObject({ ...taskObject, [name]: value });
      }

      // For validation
      if (Object.keys(requiredFields).includes(name)) {
        setRequiredFields({ ...requiredFields, [name]: value });
      }
    },
    [requiredFields, taskObject]
  );

  const handleRemoveSharedWith = (emp: string) => {
    let removedEmpName: string;
    const spaces = emp.split(' ').length - 1;
    if (spaces === 2) {
      removedEmpName = emp.split(' ').slice(0, 2).join('.');
    } else {
      const splittedArray = emp.split(' ');
      removedEmpName = `${splittedArray[0] + splittedArray[1]}.${
        splittedArray[2]
      }`;
    }
    const removedEmpId = emp.split(/[\s+]+/).pop() || '';
    // Update sharedWith in task object
    const newSharedWith = taskObject.sharedWith.filter((item: string) => {
      return item.includes(removedEmpId?.toLowerCase()) === false;
    });
    setTaskObject({
      ...taskObject,
      sharedWith: newSharedWith
    });

    // update Emails for sharing
    const emailsAfterRemove = shareObject.email.filter((email: string) => {
      const existingString = email.split('@')[0];
      return existingString !== removedEmpName;
    });
    setShareObject({ ...shareObject, email: emailsAfterRemove });
  };

  const handleRemoveAttachStoplight = (
    e: MouseEvent<HTMLNeuIconElement>,
    sl: Stoplight
  ) => {
    e.stopPropagation();
    dispatch(setToastType('Delete Stoplight'));
    if (sl.id === taskObject.stoplightId) {
      toast(
        `Are you sure you would like to un-attach stoplight ${sl.friendlyId}?`,
        'info',
        null,
        400,
        true,
        null,
        () => {
          dispatch(clearStoplight());
        }
      );
    } else if (taskObject.stoplightId) {
      dispatch(getStoplightById(taskObject.stoplightId));
    } else {
      dispatch(clearStoplight());
    }
  };

  const sharedWithDisplay = useMemo(
    () =>
      sharedEmployeesData?.map((emp: Employee) => {
        const empId = emp.employeeId?.toUpperCase();
        return `${emp.firstName} ${emp.lastName} ${empId}`;
      }) ?? [],
    [sharedEmployeesData]
  );

  const handleClickCreateStoplight = () => {
    dispatch(
      saveTaskData({
        taskObject,
        shareObject,
        sharedWithDisplay
      })
    );
    dispatch(
      saveTaskModalState({
        modal: true,
        modalType,
        modalAction,
        redirect: true
      })
    );
    // Open stoplight modal from issue panel
    dispatch(setModalType('Add Stoplight'));
    dispatch(setModalAction(modalAction));
    dispatch(openModal());
  };

  // Search and select subCategory
  const handleSearchCategory = (e: any) => {
    const target = e.target as HTMLInputElement;
    setSubCategorySearchTerm(target.value);
  };

  const setSubCategory = (selectedItem: string) => {
    setTaskObject({ ...taskObject, taskCategory: [selectedItem] });
  };

  // Search and select Service Central categories
  const handleSearchSCLocation = (e: any) => {
    const target = e.target as HTMLInputElement;
    setSCLocationSearchTerm(target.value);
  };

  const setRequiredFieldValues = (taskObj: TaskPost) => {
    const {
      unitId,
      category,
      subCategory,
      description,
      employeeFirstName,
      roundingType
    } = taskObj;
    let tempRequiredValues;
    if (taskObj.roundingType === 'patient') {
      tempRequiredValues = {
        category,
        ...(taskObj.category && subcategoryNames.length > 0
          ? { subCategory }
          : {}),
        unitId,
        description,
        roundingType
      };
    } else {
      tempRequiredValues = {
        category,
        ...(taskObj.category && subcategoryNames.length > 0
          ? { subCategory }
          : {}),
        description,
        employeeFirstName,
        roundingType
      };
    }

    if (serviceCentralCategories.includes(category as string)) {
      if (
        category === 'Maintenance/Access (Patient/Visitor)' ||
        category === 'Maintenance/Access (Staff)' ||
        category === 'Building Maintenance (Public)' ||
        category === 'Building Maintenance (Staff)' ||
        category === 'Building Maintenance'
      ) {
        setRequiredFields({
          ...tempRequiredValues,
          callback: taskObject.callback || taskObject.u_best_contact_info || '',
          u_best_contact_info: taskObject.u_best_contact_info,
          u_issue_location: taskObject.u_issue_location || '',
          ec_loc: taskObject.ec_loc || ''
        });
      } else if (
        category === 'GE Biomed (ServiceCentral)' ||
        category === 'GE Biomed'
      ) {
        if (haveControlNumber === true) {
          setRequiredFields({
            ...tempRequiredValues,
            callback: taskObject.callback || '',
            u_best_contact_info: taskObject.u_best_contact_info,
            u_control_number: taskObject.u_control_number || '',
            u_ge_loc: taskObject.u_ge_loc || ''
          });
        } else {
          setRequiredFields({
            ...tempRequiredValues,
            callback: taskObject.callback || '',
            u_issue_location: taskObject.u_issue_location || '',
            u_best_contact_info: taskObject.u_best_contact_info,
            u_ge_loc: taskObject.u_ge_loc || '',
            u_reason_for_no_control_number:
              taskObject.u_reason_for_no_control_number || ''
          });
        }
      } else if (
        category === 'ITG' ||
        category === 'IT&S' ||
        category === 'ITG (ServiceCentral)' ||
        category === 'IT&S (ServiceCentral)'
      ) {
        setRequiredFields({
          ...tempRequiredValues,
          callback: taskObject.callback || '',
          u_issue_location: taskObject.u_issue_location || '',
          u_best_contact_info: taskObject.u_best_contact_info,
          taskCategory: taskObject.taskCategory || ''
        });
      } else if (category === 'Supply Chain - Unit Room Item Request') {
        setRequiredFields({
          ...tempRequiredValues,
          callback: taskObject.callback || '',
          u_best_contact_info: taskObject.u_best_contact_info,
          ec_loc: taskObject.ec_loc || '',
          u_issue_location: taskObject.u_issue_location || ''
        });
      }
    } else {
      setRequiredFields({ ...tempRequiredValues });
    }
  };

  const setSCLocation = (selectedItem: SCLocation) => {
    if (
      taskObject.category === 'GE Biomed (ServiceCentral)' ||
      taskObject.category === 'GE Biomed'
    ) {
      setTaskObject({ ...taskObject, u_ge_loc: selectedItem.sys_id });
    } else {
      setTaskObject({ ...taskObject, ec_loc: selectedItem.sys_id });
    }
  };

  const controlNumberOptionChange = () => {
    if (!haveControlNumber) {
      setHaveControlNumber(true);
    } else {
      setHaveControlNumber(false);
    }
  };

  const setPatientForIssue = (patient: Patient) => {
    setTaskObject({
      ...taskObject,
      // this accounts for UL patients that do not have an account number at initial creation
      accountNum: patient.accountNum
        ? (patient.accountNumber as string)
        : patient.mrn,
      patientFirstName: patient.firstname as string,
      patientLastName: patient.lastname as string,
      patientFullName:
        patient.firstname && patient.lastname
          ? `${patient.firstname} ${patient.lastname}`
          : '',
      room: patient.room,
      bed: patient.bed || ''
    });
  };

  const setEmployeeToShare = (emp: Employee) => {
    const empId = emp.employeeId;
    const empExists = taskObject.sharedWith.includes(empId);
    // if the employee already exists in share, do not add again
    if (!edit && empExists) {
      setShareObject({
        taskId: taskObject.taskId,
        userName: `${authorizedUser.firstName} ${authorizedUser.lastName}`,
        email: [...shareObject.email, emp.email]
      });
    } else if (!empExists) {
      setTaskObject({
        ...taskObject,
        sharedWith: [...taskObject.sharedWith, empId]
      });
      setShareObject({
        taskId: taskObject.taskId,
        userName: `${authorizedUser.firstName} ${authorizedUser.lastName}`,
        email: [...shareObject.email, emp.email]
      });
    } else {
      // TODO future development add a toast here saying 'user already selected'
      console.log('User already selected');
    }
  };

  const setEmployeeForIssue = (emp: Employee) => {
    setTaskObject({
      ...taskObject,
      employeeId: emp.employeeId,
      employeeFirstName: emp.firstName,
      employeeLastName: emp.lastName,
      employeeFullName:
        emp.firstName && emp.lastName ? `${emp.firstName} ${emp.lastName}` : '',
      department: emp.department,
      departmentCode: emp.departmentCode,
      departmentId: emp.departmentId
    });
    // For validation
    setRequiredFields({ ...requiredFields, employeeFirstName: emp.firstName });
  };

  // adding stoplight to issue
  const setStoplightToAttach = (sl: Stoplight) => {
    if (sl?.id) {
      dispatch(getStoplightById(sl.id));
    }
  };

  // adding new stoplight to issue
  const setNewStoplightToAttach = (id: string) => {
    if (id) {
      dispatch(getStoplightById(id));
    }
  };

  const handleClose = () => {
    if (modalAction === 'UNIVERSAL-ADD') {
      logTrackingEvent(logAdHocClick(modalType, 'close'));
    } else {
      logTrackingEvent(logTaskClick('issues', 'close'));
    }
    dispatch(clearPatientSearch());
    // Clear temporarily stored task data
    dispatch(clearTaskData());
    dispatch(clearTaskModalState());
    dispatch(clearStoplight());
    setShareObject({ taskId: '', userName: '', email: [] });
    handleModal(modalContentRef);
    if (deepLinkTaskView) {
      navigate(issueRedirectPath);
    }
  };

  const handlePost = () => {
    if (showButton) {
      // Remove firstName and lastName from final 'draft' of taskObject before posting
      delete taskObject.firstName;
      delete taskObject.lastName;
      delete taskObject.title;
      const payload = { ...taskObject };
      console.log(payload);
      payload.roundingType =
        payload.roundingType !== 'csc' ? payload.roundingType : 'csrn';
      payload.stoplightId =
        attachedStoplight && Object.keys(attachedStoplight).length > 0
          ? attachedStoplight.id
          : '';
      if (
        shareObject.taskId &&
        shareObject.userName &&
        shareObject.email.length > 0
      ) {
        dispatch(addTaskToSharingQ(shareObject));
      }
      if (edit) {
        const updatedIssue = {
          ...payload,
          facilityId: isIlobRounder
            ? ''
            : payload.facilityId
            ? payload.facilityId
            : payload?.taskId?.split(':')[1],
          updated: date.now()
        };
        delete updatedIssue.discussion;

        dispatch(postNewTask(updatedIssue));
      } else if (isRoundStarted && !edit) {
        dispatch(setPendingIssue(payload));
      } else {
        dispatch(postNewTask(payload));
      }
      dispatch(clearPatientSearch());
      // Clear temporarily stored task data
      dispatch(clearTaskData());
      dispatch(clearTaskModalState());
      dispatch(clearStoplight());
      handleModal(modalContentRef);
      if (deepLinkTaskView) {
        navigate(issueRedirectPath);
      }
    }
  };

  const handleRedirect = () => {
    if (taskObject.roundingType === 'patient') {
      if (taskObject.facilityId && taskObject?.accountNum) {
        handleModal(modalContentRef);
        const patientMeta = {
          facilityId: taskObject.facilityId,
          accountNum: taskObject.accountNum,
          unitId: taskObject.unitId
        };
        if (taskObject?.accountNum?.substring(0, 2) === 'UL') {
          navigate('/patients/profile', {
            state: {
              patientMeta: {
                ...patientMeta,
                firstName: taskObject.patientFirstName,
                lastName: taskObject.patientLastName,
                mrn: taskObject.patientMRN,
                room: taskObject.room,
                bed: taskObject.bed,
                lastRoundId: taskObject.roundId
              },
              isUnlistedPatient: true
            }
          });
        } else {
          navigate('/patients/profile', {
            state: {
              patientMeta,
              isUnlistedPatient: false
            }
          });
        }
      }
    }
    if (
      taskObject.roundingType === 'employee' ||
      taskObject.roundingType === 'csrn' ||
      taskObject.roundingType === 'cned'
    ) {
      handleModal(modalContentRef);
      let profileRedirectPath = '';
      switch (taskObject.roundingType) {
        case 'employee':
          profileRedirectPath = '/employees/profile';
          break;
        default:
          profileRedirectPath = `/support/profile/${taskObject.roundingType}`;
          break;
      }
      navigate(profileRedirectPath, {
        state: {
          from: pathname,
          employeeId: issueData?.employeeId,
          mode: undefined
        }
      });
    }
  };
  /* Code block for navigating to round from issue modal */

  const handleRemoveChip = (type: string) => {
    if (type === 'patient') {
      setTaskObject({
        ...taskObject,
        accountNum: '',
        patientFirstName: '',
        patientLastName: '',
        patientFullName: '',
        room: '',
        bed: ''
      });
    } else {
      setTaskObject({
        ...taskObject,
        employeeId: '',
        employeeFirstName: '',
        employeeLastName: '',
        employeeFullName: '',
        department: '',
        departmentCode: '',
        departmentId: ''
      });
    }
  };

  const handlePostComment = useCallback(() => {
    if (comment) {
      dispatch(
        postTaskComment({
          task: taskObject,
          comment: {
            id: issueData?.taskId,
            comment: {
              body: comment,
              authorId: authorizedUser.hcaid,
              authorFullName: `${authorizedUser.firstName} ${authorizedUser.lastName}`
            }
          }
        })
      );
      setComment('');
    }
  }, [comment, issueData, authorizedUser]);

  const unitName = useMemo(() => {
    const foundUnit: Unit[] = units.filter(
      (unit: Unit) => unit.unitId === taskObject.unitId
    );
    return foundUnit[0]?.unit;
  }, [taskObject, units]);

  const isAnimated = useMemo(
    () => (!tempModalState?.redirect ? 'show-modal' : ''),
    [tempModalState]
  );

  const hasStoplightView = useMemo(
    () =>
      taskObject?.roundingType === 'patient' ||
      taskObject?.roundingType === 'employee',
    [taskObject?.roundingType]
  );

  const roundingTypesForAdHoc = useMemo(() => {
    const choices = [];
    if (isCNEdRounder) {
      choices.push('cned');
    }
    if (isCSCRounder) {
      choices.push('csc');
    }
    if (isNonSupportRounder) {
      choices.push('employee', 'patient');
    }
    return choices;
  }, [issueTypes]);

  const filteredSubCategories = useMemo(() => {
    const searchKey = subCategorySearchTerm.trim().toUpperCase();
    const resultFacilities = itAndSCategories.filter((item: string) => {
      return item.toUpperCase().includes(searchKey);
    });
    return resultFacilities;
  }, [itAndSCategories, subCategorySearchTerm]);

  const filteredLocations = useMemo(() => {
    const isGEBiomed =
      taskObject.category === 'GE Biomed (ServiceCentral)' ||
      taskObject.category === 'GE Biomed';

    const searchKey = scLocationSearchTerm.trim().toUpperCase();
    if (isGEBiomed) {
      return geLocations.filter((item: GELocation) =>
        item.name.toUpperCase().includes(searchKey)
      );
    }
    return scLocations.filter((item: SCLocation) =>
      item.name.toUpperCase().includes(searchKey)
    );
  }, [taskObject, geLocations, scLocations, scLocationSearchTerm]);

  const selectedSCLocation = useMemo(() => {
    if (taskObject.ec_loc || taskObject.u_ge_loc) {
      const selectedSCLoc =
        scLocations.length &&
        scLocations.filter(item => item.sys_id === taskObject.ec_loc);
      if (selectedSCLoc && selectedSCLoc.length === 1) {
        return selectedSCLoc[0]?.name;
      }
      const selectedGELoc =
        geLocations.length &&
        geLocations.filter(item => item.sys_id === taskObject.u_ge_loc);
      if (selectedGELoc && selectedGELoc.length === 1) {
        return selectedGELoc[0]?.name;
      }
      return '';
    }
    return '';
  }, [taskObject, scLocations, geLocations]);

  useEffect(() => {
    if (serviceCentralCategories.includes(taskObject.category as string)) {
      if (
        taskObject.category === 'GE Biomed (ServiceCentral)' ||
        taskObject.category === 'GE Biomed'
      ) {
        dispatch(getGELocations());
      } else {
        dispatch(getSCLocations());
      }
    }
  }, [taskObject.category]);

  // modifying required fields based on rounding type
  useEffect(() => {
    const copyTaskObject = { ...taskObject };
    if (!serviceCentralCategories.includes(taskObject.category as string)) {
      delete copyTaskObject.callback;
      delete copyTaskObject.u_best_contact_info;
      delete copyTaskObject.u_issue_location;
      delete copyTaskObject.u_control_number;
      delete copyTaskObject.ec_loc;
      delete copyTaskObject.u_ge_loc;
      delete copyTaskObject.taskCategory;
      setTaskObject(copyTaskObject);
    }
    setRequiredFieldValues(copyTaskObject);
  }, [
    taskObject.roundingType,
    taskObject.category,
    taskObject.taskCategory,
    taskObject.ec_loc,
    taskObject.u_ge_loc,
    taskObject.sys_id,
    taskObject.subCategory,
    haveControlNumber,
    impactPatient
  ]);

  useEffect(() => {
    // Get discussion in edit mode for non-service central issues
    // Code block for navigating to round from issue modal
    if (
      edit &&
      !serviceCentralCategories.includes(taskObject.category as string)
    )
      dispatch(getTaskDiscussion(issueData.taskId));

    if (edit && taskObject) {
      if (taskObject?.roundId && pathname.includes('/profile'))
        dispatch(
          getRound({
            roundId: taskObject.roundId,
            fromHistoryDrawer: false,
            type: taskObject.roundingType,
            ...(taskObject.roundingType === 'patient'
              ? { accountNum: taskObject.accountNum, unitId: taskObject.unitId }
              : {})
          })
        );
    } else if (
      !issueData?.roundId &&
      !(modalAction === 'UNIVERSAL-ADD' && modalType === 'Add Issue') &&
      !modalType.includes('Issue On Rounds')
    ) {
      dispatch(clearRoundState());
    }

    return () => {
      // clear out on dismount
      if (stoplight && stoplight.id) {
        dispatch(clearTaskModalState());
        dispatch(clearTaskData());
        dispatch(clearStoplight());
      }
      // TODO: Is this needed?
      // if (!taskObject.stoplightId) {
      //   dispatch(clearStoplight());
      // }
    };
  }, []);

  // getting issue types to populate dropdowns, setting issue origin based on location
  useEffect(() => {
    if (issueTypes.includes(taskObject.roundingType)) {
      let categoryFor;
      switch (taskObject.roundingType) {
        case 'patient':
          categoryFor = 'patient';
          break;
        default:
          categoryFor = 'employee';
          break;
      }
      dispatch(
        getCategoriesByType({
          categoryFor,
          categoryType: 'issue'
        })
      );
    }
  }, [taskObject.roundingType]);

  // shared with search
  useEffect(() => {
    getEmployeesBySearch(dbSharedSearchTerm);
  }, [dbSharedSearchTerm]);

  useEffect(() => {
    if (employeesBySearchData?.length > 0 && dbSharedSearchTerm.length > 0) {
      setSharedPopState(true);
    }
  }, [employeesBySearchData]);

  // Validate fields on change and disable button
  useEffect(() => {
    let count = 0;
    Object.keys(requiredFields).forEach(item => {
      const tempVar = requiredFields[item];
      if (!tempVar || (Array.isArray(tempVar) && tempVar.length === 0)) {
        count += 1;
      }
      if (count === 0) {
        setShowButton(true);
      } else {
        setShowButton(false);
      }
    });
  }, [requiredFields]);

  useEffect(() => {
    const tempTaskObj = {
      ...taskObject,
      ...tempTaskObject
    };
    if (issueData && modalType === 'Add Patient Issue On Rounds') {
      const patientTaskObj: Task = {
        ...tempTaskObj,
        roundingType: userSection,
        taskId: createAnyId('task', authorizedUser.facilityId),
        taskDate: date.now(),
        unitId: issueData.unitId,
        facilityId: authorizedUser.facilityId,
        updatedBy: authorizedUser.hcaid,
        userId: authorizedUser.hcaid,
        accountNum: issueData?.accountNum ?? issueData.mrn,
        patientFirstName: issueData.firstName as string,
        patientLastName: issueData.lastName as string,
        patientFullName:
          issueData.firstName && issueData.lastName
            ? `${issueData.firstName} ${issueData.lastName}`
            : '',
        room: issueData.room,
        bed: issueData.bed as string,
        userFullName: taskObject.userFullName as string,
        taskSubtype: issueData.taskSubtype || ''
      };
      setTaskObject(patientTaskObj);
    } else if (
      issueData &&
      (modalType === 'Add Employee Issue On Rounds' ||
        modalType === 'Add Support Issue On Rounds')
    ) {
      const employeeTaskObj: Task = {
        ...tempTaskObj,
        roundingType: userSection,
        taskId: createAnyId('task', authorizedUser.facilityId, isIlobRounder),
        taskDate: date.now(),
        facilityId: isIlobRounder ? '' : authorizedUser.facilityId,
        updatedBy: authorizedUser.hcaid,
        userId: authorizedUser.hcaid,
        employeeFirstName: (issueData.firstName ??
          issueData.employeeFirstName) as string,
        employeeLastName: (issueData.lastName ??
          issueData.employeeLastName) as string,
        employeeFullName:
          (issueData.firstName ?? issueData.employeeFirstName) &&
          (issueData.lastName ?? issueData.employeeLastName)
            ? `${issueData.firstName ?? issueData.employeeFirstName} ${
                issueData.lastName ?? issueData.employeeLastName
              }`
            : '',
        employeeId: issueData.hcaid ?? issueData.employeeId,
        department: issueData.department,
        departmentCode: issueData.departmentCode,
        departmentId: issueData.departmentId,
        userFullName: taskObject.userFullName as string,
        taskSubtype: issueData.taskSubtype || ''
      };
      setTaskObject(employeeTaskObj);
    } else {
      setTaskObject({ ...tempTaskObj });
    }
    if (edit && issueData) {
      setTaskObject({
        ...tempTaskObj,
        category:
          tempTaskObj.category || tempTaskObj.taskSubtype || issueData.category,
        // THIS IS A BUG
        patientFullName:
          issueData.patientFirstName && issueData.patientLastName
            ? `${issueData.patientFirstName} ${issueData.patientLastName}`
            : '',
        sharedWith: issueData.sharedWith ? [...issueData.sharedWith] : []
      });
      // Get attached stoplight details
      if (taskObject.stoplightId) {
        dispatch(getStoplightById(taskObject.stoplightId));
      }
      // Set default selection in edit mode
      if (!issueData.u_control_number) {
        setHaveControlNumber(false);
      } else {
        setHaveControlNumber(true);
      }
    }

    if (subcategoryNames.length > 0) {
      if (userSection === 'patient') {
        setRequiredFields({
          category: tempTaskObj.category || taskObject.category || '',
          subCategory: tempTaskObj.subCategory || taskObject.subCategory || '',
          description: tempTaskObj.description || taskObject.description || '',
          unitId: tempTaskObj.unitId || taskObject.unitId || ''
        });
      } else {
        setRequiredFields({
          category: tempTaskObj.category || taskObject.category || '',
          subCategory: tempTaskObj.subCategory || taskObject.subCategory || '',
          description: tempTaskObj.description || taskObject.description || ''
        });
      }
    } else if (userSection === 'patient') {
      setRequiredFields({
        category: tempTaskObj.category || taskObject.category || '',
        description: tempTaskObj.description || taskObject.description || '',
        unitId: tempTaskObj.unitId
      });
    } else {
      setRequiredFields({
        category: tempTaskObj.category || taskObject.category || '',
        description: tempTaskObj.description || taskObject.description || ''
      });
    }
  }, [edit, issueData, modalType, tempTaskObject]);

  useEffect(() => {
    if (!edit && tempModalState?.modal) {
      sharedEmployeesData?.forEach((emp: Employee) => setEmployeeToShare(emp));
    }
  }, [sharedEmployeesData]);

  useEffect(() => {
    if (controlNumberResponse) {
      handlePost();
    }
  }, [controlNumberResponse]);

  useEffect(() => {
    if (taskObject?.sharedWith && taskObject.sharedWith.length > 0) {
      getEmployeesByIds([...taskObject.sharedWith]);
    }
  }, [taskObject.sharedWith]);

  // Auto attach created stoplight with issue
  useEffect(() => {
    if (tempTaskObject?.stoplightId) {
      dispatch(clearStoplight());
      setNewStoplightToAttach(tempTaskObject.stoplightId);
    }
  }, [tempTaskObject?.stoplightId]);

  return (
    <AddIssueCard
      id="Modal-Add-Issues"
      className={isAnimated}
      unit="card"
      ref={modalContentRef}
    >
      <CardHeader className="neu-bg-plain-0" unit="header">
        <CardMainHeader>
          {!edit && (
            <CardTitle>
              <TitleSpan>
                <TitleIcon size="3x">flag</TitleIcon>
                Add Issue
              </TitleSpan>
            </CardTitle>
          )}
          {edit && !taskObject.completed && (
            <MarkCompleteBtn edit taskId={taskObject.taskId} taskType="issue" />
          )}
          {edit && taskObject.completed && (
            <StyledBoldHeading className="mt-0" autoWidth>
              {taskObject.roundingType === 'patient' ? 'Patient' : 'Employee'} |{' '}
              {taskObject.category}
            </StyledBoldHeading>
          )}
          <NeuButton
            aria-label="close panel"
            kind="tertiary"
            color="blue"
            icon="close"
            size="small"
            onClick={handleClose}
          >
            Close
          </NeuButton>
        </CardMainHeader>
        {hasStoplightView && (
          <IssueSubHeader
            completed={taskObject.completed}
            items={attachedStoplight?.id}
            type="issue"
            view={view}
            setView={setView}
          />
        )}
      </CardHeader>
      {view === 'info' && (
        <ModalCardContent unit="content">
          {edit && (
            <>
              <LabelHeading>
                {userSection !== 'patient' &&
                  `${formatRoundingType(taskObject.roundingType)} `}
                Issue ID
              </LabelHeading>
              <LabelContentBold className="mt-1">
                {taskObject.friendlyId ? taskObject.friendlyId : 'Unknown'}
              </LabelContentBold>
            </>
          )}
          {!(
            modalType === 'Add Patient Issue On Rounds' ||
            modalType === 'Add Employee Issue On Rounds' ||
            modalType === 'Add Support Issue On Rounds'
          ) &&
          (!edit ||
            (!taskObject.completed &&
              (isEnterpriseAdmin ||
                (taskObject.taskDate &&
                  authorizedUser.hcaid === taskObject.userId &&
                  !date.isOverOneDayOld(taskObject.taskDate))))) ? (
            <div>
              {modalAction === 'UNIVERSAL-ADD' && (
                <>
                  <InputHeading>Issue Origin</InputHeading>
                  <RoundingTypeDropdown
                    name="issue-rounding-type"
                    open={openRoundingType}
                    options={roundingTypesForAdHoc}
                    placeholder={
                      taskObject?.roundingType &&
                      roundingTypesForAdHoc.includes(taskObject?.roundingType)
                        ? formatRoundingType(taskObject?.roundingType)
                        : 'Select Origin'
                    }
                    selectedRoundingType={taskObject?.roundingType}
                    setSelectedRoundingType={changeIssueOrigin}
                    setOpen={setOpenRoundingType}
                  />
                </>
              )}
              {taskObject.roundingType && (
                <>
                  <InputHeading>
                    {taskObject.roundingType === 'patient'
                      ? 'Patient (Optional)'
                      : 'Employee'}
                  </InputHeading>
                  {taskObject.roundingType === 'patient' ? (
                    <PatientSearch
                      chipVal={
                        taskObject.patientFullName
                          ? reverseName(
                              taskObject.patientFirstName,
                              taskObject.patientLastName
                            )
                          : ''
                      }
                      showLabel={false}
                      val={taskObject.patientFullName ?? ''}
                      handleRemovePatient={handleRemoveChip}
                      setPatientSelection={setPatientForIssue}
                    />
                  ) : (
                    <EmployeeSearch
                      chipVal={
                        taskObject.employeeFullName
                          ? text.lastFirst(taskObject.employeeFullName.trim())
                          : taskObject.employeeFirstName
                          ? `${toTitleCase(
                              taskObject.employeeFirstName
                            )} ${toTitleCase(taskObject.employeeLastName)}`
                          : ''
                      }
                      showLabel={false}
                      val={taskObject.employeeId ?? ''}
                      handleRemoveEmployee={handleRemoveChip}
                      setEmployeeSelection={setEmployeeForIssue}
                    />
                  )}
                </>
              )}
            </div>
          ) : (
            <div>
              <StyledEditHeaderRow className="px-0">
                <InputHeading style={{ flexBasis: '61%' }}>
                  {userSection === 'patient' ? 'Patient' : 'Employee'}
                </InputHeading>
                {userSection === 'patient' ? (
                  <InputHeading>Room</InputHeading>
                ) : (
                  ''
                )}
              </StyledEditHeaderRow>

              <StyledEditHeaderRow className="px-0">
                <StyledBoldHeading
                  className="mt-1"
                  color={
                    taskObject &&
                    taskObject.facilityId &&
                    taskObject.accountNum &&
                    taskObject.roundId &&
                    (taskObject.patientFullName || taskObject.employeeId)
                      ? '#206DA3'
                      : '#191919'
                  }
                  cursorType={
                    !!(
                      taskObject &&
                      taskObject.facilityId &&
                      taskObject.accountNum &&
                      taskObject.roundId &&
                      (taskObject.patientFullName || taskObject.employeeId)
                    )
                  }
                  style={{ flexBasis: '61%' }}
                  onClick={
                    taskObject &&
                    taskObject.facilityId &&
                    taskObject.accountNum &&
                    taskObject.roundId &&
                    (taskObject.patientFullName || taskObject.employeeId)
                      ? () => {
                          handleRedirect();
                        }
                      : undefined
                  }
                >
                  {taskObject.roundingType === 'patient'
                    ? toTitleCase(
                        text.lastFirst(taskObject.patientFullName) || 'Unknown'
                      )
                    : taskObject.employeeFullName
                    ? text.lastFirst(taskObject.employeeFullName)
                    : taskObject.employeeLastName &&
                      taskObject.employeeFirstName
                    ? `${toTitleCase(
                        taskObject.employeeFirstName
                      )} ${toTitleCase(taskObject.employeeLastName)}`
                    : 'Unknown'}
                </StyledBoldHeading>
                {userSection === 'patient' && (
                  <StyledBoldHeading className="mt-1">
                    {taskObject.room
                      ? `${taskObject.room}${taskObject.bed || ''}`
                      : 'N/A'}
                  </StyledBoldHeading>
                )}
              </StyledEditHeaderRow>
            </div>
          )}
          {edit && issueData?.userFullName && (
            <>
              <StyledEditHeaderRow className="px-0 pb-0">
                <InputHeading className="mt-1">Creator</InputHeading>
              </StyledEditHeaderRow>
              <StyledEditHeaderRow className="px-0 pt-0">
                <StyledBoldHeading className="mt-1">
                  {issueData?.userFullName}
                </StyledBoldHeading>
              </StyledEditHeaderRow>
            </>
          )}
          {taskObject.roundingType === 'patient' && (
            <>
              <InputHeading>Unit</InputHeading>
              {!edit ||
              (!taskObject.completed &&
                (isEnterpriseAdmin ||
                  (authorizedUser.hcaid === taskObject.userId &&
                    !taskObject.completed &&
                    taskObject.taskDate &&
                    !date.isOverOneDayOld(taskObject.taskDate)))) ? (
                // TODO: Needs to be a custom dropdown
                <Dropdown
                  classes="ps-0 pe-2"
                  name="unitId"
                  open={openUnit}
                  placeholder={
                    units.find(
                      (unit: Unit) => unit.unitId === taskObject.unitId
                    )?.unit || 'Select a Unit'
                  }
                  setOpen={setOpenUnit}
                >
                  {[...units]
                    .sort(compareObjByStringKey('unit'))
                    .map((unit: Unit) => (
                      <div
                        key={unit.unitId}
                        id={`Modal-Unit-Options-${unit.unitId}`}
                        aria-selected={unit.unitId === taskObject.unitId}
                        className={`dropdown-hover-item${
                          taskObject.unitId === unit.unitId ? ' selected' : ''
                        }`}
                        role="option"
                        tabIndex={0}
                        onClick={() =>
                          handleDropdownChange(unit.unitId, 'unitId')
                        }
                        onKeyDown={() =>
                          handleDropdownChange(unit.unitId, 'unitId')
                        }
                      >
                        {unit.unit}
                      </div>
                    ))}
                </Dropdown>
              ) : (
                <StyledBoldLabel className="mt-1">{unitName}</StyledBoldLabel>
              )}
            </>
          )}
          {edit &&
            !isEnterpriseAdmin &&
            !taskObject.completed &&
            taskObject.taskDate &&
            (date.isOverOneDayOld(taskObject.taskDate) ||
              !(authorizedUser.hcaid === taskObject.userId)) && (
              <>
                <InputHeading>Issue Category</InputHeading>
                <StyledBoldLabel className="d-flex align-items-center">
                  {serviceCentralCategories.includes(
                    taskObject.category as string
                  ) && (
                    <NeuImage
                      width="25"
                      alt="service central logo"
                      src={serviceCentralLogoImg}
                    />
                  )}
                  {taskObject.category}
                </StyledBoldLabel>
              </>
            )}
          {((authorizedUser.hcaid === taskObject.userId &&
            !taskObject.completed &&
            taskObject.taskDate &&
            !date.isOverOneDayOld(taskObject.taskDate)) ||
            (isEnterpriseAdmin && !taskObject.completed)) && (
            <CategorySelect
              disabled={!issueTypes.includes(taskObject.roundingType)}
              heading="Issue Category"
              categoryName="category"
              categories={categoryNames}
              categoryValue={taskObject.category || ''}
              open={openCat}
              placeholder="Select a Category"
              handleInputs={handleDropdownChange}
              setOpen={setOpenCat}
            />
          )}
          {edit &&
            taskObject.subCategory &&
            ((!isEnterpriseAdmin &&
              taskObject.taskDate &&
              (date.isOverOneDayOld(taskObject.taskDate) ||
                !(authorizedUser.hcaid === taskObject.userId))) ||
              taskObject.completed) && (
              <>
                <InputHeading>Issue Subcategory</InputHeading>
                <StyledBoldLabel className="mt-1">
                  {taskObject.subCategory}
                </StyledBoldLabel>
              </>
            )}
          {((authorizedUser.hcaid === taskObject.userId &&
            !taskObject.completed &&
            taskObject.taskDate &&
            !date.isOverOneDayOld(taskObject.taskDate)) ||
            (isEnterpriseAdmin && !taskObject.completed)) && (
            // TODO: Need to clean this up to not be selectable when supply chain is selected
            <CategorySelect
              disabled={
                !taskObject.category ||
                taskObject.category.includes('Supply Chain') ||
                subcategoryNames.length === 0
              }
              heading="Issue Subcategory"
              categoryName="subCategory"
              categories={subcategoryNames}
              categoryValue={
                taskObject.category &&
                !taskObject.category.includes('Supply Chain')
                  ? taskObject.subCategory || ''
                  : ''
              }
              open={openSubcat}
              placeholder={
                taskObject.category &&
                !taskObject.category.includes('Supply Chain') &&
                subcategoryNames.length === 0
                  ? 'No subcategories'
                  : 'Select a Subcategory'
              }
              handleInputs={(e: any) => handleDropdownChange(e, 'subCategory')}
              setOpen={setOpenSubcat}
            />
          )}
          {serviceCentralCategories.includes(taskObject.category as string) && (
            <ServiceCentralSection>
              {(taskObject.category === 'ITG' ||
                taskObject.category === 'IT&S' ||
                taskObject.category === 'ITG (ServiceCentral)' ||
                taskObject.category === 'IT&S (ServiceCentral)') && (
                <>
                  <InputHeading>Category</InputHeading>
                  {/* TODO: Needs to be a custom dropdown */}
                  <Dropdown
                    id="task-category-dropdown"
                    name="taskCategory"
                    classes="ps-0 pe-2"
                    focused={activeSearch === 'taskCategory'}
                    open={subCategoryPopState}
                    placeholder={
                      (taskObject.taskCategory && taskObject.taskCategory[0]) ||
                      'Choose issue type'
                    }
                    query={subCategorySearchTerm}
                    typeAhead
                    setOpen={setSubCategoryPopState}
                    setTypeAhead={setActiveSearch}
                    setTypeAheadChange={handleSearchCategory}
                  >
                    {filteredSubCategories.map((item: string) => (
                      <div
                        key={item}
                        className="dropdown-hover-item"
                        aria-selected={
                          taskObject.taskCategory
                            ? item === taskObject.taskCategory[0]
                            : false
                        }
                        role="option"
                        tabIndex={0}
                        onMouseDown={() => {
                          setSubCategory(item);
                          setActiveSearch('');
                          setSubCategorySearchTerm('');
                        }}
                        onKeyDown={() => {
                          setSubCategory(item);
                          setActiveSearch('');
                          setSubCategorySearchTerm('');
                        }}
                      >
                        {item}
                      </div>
                    ))}
                  </Dropdown>
                  <NeuInput
                    name="u_issue_location"
                    autocomplete="off"
                    autoCorrect="off"
                    label="Issue Location (Please note the issue's physical location.)"
                    required
                    spellCheck
                    value={taskObject.u_issue_location}
                    onNeuChange={handleInputs}
                  />
                </>
              )}
              {(taskObject.category ===
                'Maintenance/Access (Patient/Visitor)' ||
                taskObject.category === 'Maintenance/Access (Staff)' ||
                taskObject.category === 'Building Maintenance (Public)' ||
                taskObject.category === 'Building Maintenance (Staff)' ||
                taskObject.category === 'Building Maintenance' ||
                taskObject.category === 'GE Biomed (ServiceCentral)' ||
                taskObject.category === 'GE Biomed' ||
                taskObject.category ===
                  'Supply Chain - Unit Room Item Request') && (
                <>
                  <InputHeading>Facility</InputHeading>
                  {/* TODO: Needs to be a custom dropdown */}
                  <Dropdown
                    id="ec_loc-dropdown"
                    name="ec_loc"
                    classes="ps-0 pe-2"
                    focused={activeSearch === 'ec_loc'}
                    open={scLocationPopState}
                    placeholder={
                      taskObject.ec_loc || taskObject.u_ge_loc
                        ? selectedSCLocation
                        : 'Select a Location'
                    }
                    query={scLocationSearchTerm}
                    typeAhead
                    setOpen={setSCLocationPopState}
                    setTypeAhead={setActiveSearch}
                    setTypeAheadChange={handleSearchSCLocation}
                  >
                    {[...filteredLocations]
                      .sort(compareObjByStringKey('name'))
                      .map((item: GELocation | SCLocation) => (
                        <div
                          key={item.sys_id}
                          className="dropdown-hover-item"
                          aria-selected={
                            taskObject.ec_loc || taskObject.u_ge_loc
                              ? item.sys_id === taskObject.ec_loc ||
                                item.sys_id === taskObject.u_ge_loc
                              : false
                          }
                          role="option"
                          tabIndex={0}
                          onMouseDown={() => {
                            setSCLocation(item);
                            setActiveSearch('');
                            setSCLocationSearchTerm('');
                          }}
                          onKeyDown={() => {
                            setSCLocation(item);
                            setActiveSearch('');
                            setSCLocationSearchTerm('');
                          }}
                        >
                          {item.name}
                        </div>
                      ))}
                  </Dropdown>
                  {(taskObject.category === 'GE Biomed (ServiceCentral)' ||
                    taskObject.category === 'GE Biomed') && (
                    <>
                      <NeuInput
                        className="my-1"
                        name="u_control_number"
                        autocomplete="off"
                        autoCorrect="off"
                        label="Control Number"
                        required
                        value={taskObject.u_control_number}
                        onNeuChange={handleInputs}
                      />
                      {taskObject.u_control_number ? (
                        ''
                      ) : (
                        <UnknownControlContainer id="Add-Issue-Modal-Checkbox">
                          <NeuCheckbox
                            id="Unknown-Control-Checkbox"
                            color="blue"
                            label="Unknown Control Number"
                            name="unknownControlNumber"
                            onClick={controlNumberOptionChange}
                          />
                        </UnknownControlContainer>
                      )}

                      {!haveControlNumber && (
                        <>
                          <NeuInput
                            name="u_reason_for_no_control_number"
                            autocomplete="off"
                            autoCorrect="off"
                            label="Provide the reason for the missing control number"
                            required
                            value={taskObject.u_reason_for_no_control_number}
                            onNeuChange={handleInputs}
                          />
                          <NeuInput
                            name="u_issue_location"
                            autocomplete="off"
                            autoCorrect="off"
                            label="Issue Location (note the issue's physical location.)"
                            required
                            spellCheck
                            value={taskObject.u_issue_location}
                            onNeuChange={handleInputs}
                          />
                        </>
                      )}
                    </>
                  )}
                  {taskObject.category.includes('Maintenance') && (
                    <NeuInput
                      name="u_issue_location"
                      autocomplete="off"
                      autoCorrect="off"
                      label="Issue Location (note the issue's physical location.)"
                      required
                      spellCheck
                      value={taskObject.u_issue_location}
                      onNeuChange={handleInputs}
                    />
                  )}
                  {taskObject.category === 'GE Biomed' && (
                    <NeuRadio
                      data-testid="add-issue-modal-impact-patient-radio-group"
                      unit="group"
                      label="Does this impact a patient?"
                      name="patientImpact"
                      required
                      value={impactPatient}
                      onNeuChange={e => handleIsImpactPatient(e.detail.value)}
                    >
                      <NeuRadio
                        data-testid="add-issue-modal-impact-patient-radio-item-yes"
                        unit="item"
                        name="patientImpact"
                        label="Yes"
                        color="blue"
                        value="yes"
                      />
                      <NeuRadio
                        data-testid="add-issue-modal-impact-patient-radio-item-no"
                        unit="item"
                        name="patientImpact"
                        label="No"
                        color="blue"
                        value="no"
                      />
                    </NeuRadio>
                  )}
                  {/* TODO: Min height needed to prevent spacing issues */}
                  {impactPatient && (
                    <StyledBoldLabel>
                      Please enter a patient event into your facility&apos;s
                      event reporting system, as well.
                    </StyledBoldLabel>
                  )}
                  {taskObject.category.includes('Supply Chain') && (
                    <>
                      <NeuRadio
                        data-testid="add-issue-modal-supply-chain-item-radio-group"
                        className="pt-1"
                        unit="group"
                        label="Item Needed"
                        name="supplyChainItemType"
                        required
                        value={typeOfItemNeed}
                        onNeuChange={e =>
                          handleSupplyChainItemType(e.detail.value)
                        }
                      >
                        <NeuRadio
                          data-testid="add-issue-modal-supply-chain-stocked-item"
                          unit="item"
                          name="supplyChainItemType"
                          label="Stocked Item"
                          color="blue"
                          value="Out of an Item"
                        />
                        <NeuRadio
                          data-testid="add-issue-modal-supply-chain-non-routine-item"
                          unit="item"
                          name="supplyChainItemType"
                          label="Non-routine Item"
                          color="blue"
                          value="Non-Routine Item Request"
                        />
                      </NeuRadio>
                      {typeOfItemNeed.includes('Non-Routine') && (
                        <NeuInput
                          data-testid="u_non_routine_justification"
                          name="u_non_routine_justification"
                          autocomplete="off"
                          autoCorrect="off"
                          label="Why is this non-routine item needed?"
                          required
                          value={taskObject.u_non_routine_justification}
                          onNeuChange={handleInputs}
                        />
                      )}
                      <NeuInput
                        data-testid="u_issue_location"
                        name="u_issue_location"
                        autocomplete="off"
                        autoCorrect="off"
                        label="Item's Location (note the item's physical location.)"
                        required
                        spellCheck
                        value={taskObject.u_issue_location}
                        onNeuChange={handleInputs}
                      />
                    </>
                  )}
                </>
              )}
              <NeuInput
                className="mt-1"
                name="callback"
                autocomplete="off"
                autoCorrect="off"
                label="Callback Number (10 digits, no dashes)"
                required
                type="tel"
                value={taskObject.callback}
                onNeuChange={handleInputs}
              />
            </ServiceCentralSection>
          )}
          {!edit ||
          (!taskObject.completed &&
            (isEnterpriseAdmin ||
              (authorizedUser.hcaid === taskObject.userId &&
                !taskObject.completed &&
                taskObject.taskDate &&
                !date.isOverOneDayOld(taskObject.taskDate)))) ? (
            <ModalTextArea
              // wrap="hard"
              name="description"
              className="mt-3"
              label="Details (Do not include PHI)"
              required
              spellCheck
              value={taskObject.description}
              onNeuChange={handleInputs}
            />
          ) : (
            <>
              <InputHeading>Details</InputHeading>
              <LabelContentBold className="mt-1">
                {taskObject.description}
              </LabelContentBold>
            </>
          )}

          <div id="Add-Issue-Modal-Share-With">
            {(!taskObject.completed ||
              (taskObject.completed && sharedWithDisplay.length > 0)) && (
              <InputHeading>
                {!taskObject.completed
                  ? 'Share With (Optional)'
                  : 'Shared With'}
              </InputHeading>
            )}

            {sharedWithDisplay.length > 0 && (
              <div className="pt-1 pb-2">
                {[...sharedWithDisplay].sort().map(emp => {
                  return (
                    <ChipWrapper
                      key={emp}
                      onClick={() => handleRemoveSharedWith(emp)}
                    >
                      <BlueChip name={emp} />
                    </ChipWrapper>
                  );
                })}
              </div>
            )}
            {!taskObject.completed && (
              <div className="position-relative">
                {/* TODO: Needs to be a custom dropdown */}
                <Dropdown
                  id="shared-with-dropdown"
                  name="sharedWith"
                  open={sharedPopState}
                  focused={activeSearch === 'sharedWith'}
                  placeholder="Start typing name or 3-4 ID"
                  query={sharedSearchTerm}
                  setOpen={setSharedPopState}
                  typeAhead
                  setTypeAhead={setActiveSearch}
                  setTypeAheadChange={handleSharedSearching}
                >
                  {[...(employeesBySearchData ?? [])]
                    .sort(compareObjByStringKey('lastName'))
                    .sort(compareObjByStringKey('firstName'))
                    .map((emp: Employee) => (
                      <div key={emp.employeeId}>
                        <div
                          className={`dropdown-hover-item${
                            taskObject.sharedWith.includes(
                              emp.employeeId?.toUpperCase()
                            )
                              ? ' selected'
                              : ''
                          }`}
                          aria-selected={taskObject.sharedWith.includes(
                            emp.employeeId
                          )}
                          role="option"
                          tabIndex={0}
                          onMouseDown={() => {
                            if (emp.email) {
                              setEmployeeToShare(emp);
                              setActiveSearch('');
                              setSharedSearchTerm('');
                              setSharedWithNoEmail(false);
                            } else {
                              setSharedWithNoEmail(true);
                            }
                          }}
                          onKeyDown={() => {
                            if (emp.email) {
                              setEmployeeToShare(emp);
                              setActiveSearch('');
                              setSharedSearchTerm('');
                              setSharedWithNoEmail(false);
                            } else {
                              setSharedWithNoEmail(true);
                            }
                          }}
                        >
                          <div className="d-flex justify-content-between">
                            <p className="mt-1 mb-0">
                              {emp.firstName.toUpperCase()}{' '}
                              {emp.lastName.toUpperCase()}
                            </p>
                            <p
                              className="mt-1 mb-0"
                              // TODO: How to better handle this thru a class
                              style={{ fontSize: '14px' }}
                            >
                              {emp.employeeId}
                            </p>
                          </div>
                          <p className="mt-1 mb-0" style={{ fontSize: '14px' }}>
                            {emp.title}
                          </p>
                          <p className="mt-1 mb-0" style={{ fontSize: '14px' }}>
                            {emp.email}
                          </p>
                        </div>
                        {[...(employeesBySearchData ?? [])].length > 1 && (
                          <StyledDivider className="my-0" />
                        )}
                      </div>
                    ))}
                </Dropdown>
              </div>
            )}
            {sharedWithNoEmail && (
              <StyledBoldLabel className="mt-2">
                This employee cannot be shared with as they do not have an email
                address in the system.
              </StyledBoldLabel>
            )}
          </div>
          {hasStoplightView && (
            <ViewStoplightsClickableLabel
              className="my-2"
              onClick={() => setView('search')}
            >
              {attachedStoplight?.id ||
              (taskObject.completed && !attachedStoplight?.id)
                ? 'View Attached Stoplight'
                : 'Attach to Stoplight'}
            </ViewStoplightsClickableLabel>
          )}
          {/* <SearchIcons
            color={item.id === taskObject.stoplightId ? 'green-60' : 'primary'}
          >
            {item.id === taskObject.stoplightId
              ? 'check'
              : 'add_circle_outline'}
          </SearchIcons> */}
          {!taskObject.completed &&
            !attachedStoplight?.id &&
            hasStoplightView && (
              <ViewStoplightsClickableLabel
                className="my-2"
                onClick={handleClickCreateStoplight}
              >
                Create New Stoplight
              </ViewStoplightsClickableLabel>
            )}

          {edit && issueData?.discussion && issueData.discussion.comments && (
            <>
              {issueData.discussion.comments.length > 0 && (
                <StyledDivider className="my-2 me-2" />
              )}
              {issueData.discussion.comments!.map((c: Comment, i: number) => (
                // eslint-disable-next-line react/no-array-index-key
                <Fragment key={i}>
                  <StyledEditHeaderRow className="my-3">
                    <div className="px-1 pt-2">
                      <NeuAvatar
                        color="blue"
                        initials={text.getInitials(c.authorFullName)}
                      />
                    </div>
                    <div>
                      <StyledEditHeaderRow className="ps-0">
                        <StyledBoldHeading
                          className="neu-text-blue-60 mt-0"
                          autoWidth
                        >
                          {c.authorFullName}
                        </StyledBoldHeading>
                        <InputHeading
                          className="mt-0 ms-2"
                          autoWidth
                          style={{ fontSize: '0.75rem' }}
                        >
                          {c.date && date.dayOfYr(c.date)}
                        </InputHeading>
                      </StyledEditHeaderRow>
                      <StyledEditHeaderRow className="ps-0">
                        <InputHeading className="mt-0">{c.body}</InputHeading>
                      </StyledEditHeaderRow>
                    </div>
                  </StyledEditHeaderRow>
                </Fragment>
              ))}
            </>
          )}
          {edit && !issueData?.completed && (
            <>
              <StyledDivider className="my-3" />
              <StyledEditHeaderCol>
                <ModalTextArea
                  name="update"
                  label="Add Comment (No PHI data)"
                  // wrap="hard"
                  spellCheck
                  value={comment}
                  onNeuChange={(e: any) => {
                    const { value } = e.target as HTMLTextAreaElement;
                    setComment(value);
                  }}
                />
                <StyledRightAlignButton
                  className="me-1"
                  color="blue"
                  size="small"
                  onClick={() => handlePostComment()}
                >
                  Add Update
                </StyledRightAlignButton>
              </StyledEditHeaderCol>
            </>
          )}
        </ModalCardContent>
      )}
      {(issueData || tempTaskObject) &&
        hasStoplightView &&
        view === 'search' && (
          // Issue info
          <ModalCardContent
            id="Issues-Modal-Stoplight-Card"
            className="pb-4"
            unit="content"
          >
            <SearchView
              attachedDisplay={
                attachedStoplight && Object.keys(attachedStoplight).length > 0
                  ? ([attachedStoplight] as (Issue & Stoplight)[])
                  : ([] as (Issue & Stoplight)[])
              }
              attachToItem={taskObject as TaskPost & Stoplight}
              attachToItemCompleted={taskObject.completed}
              prevAttached={attachedStoplight?.id ? [attachedStoplight.id] : []}
              type="stoplight"
              handleRemoveAttached={handleRemoveAttachStoplight}
              setItemsToAttach={setStoplightToAttach}
            />
          </ModalCardContent>
        )}
      <ModalFooter unit="footer" right={!edit ? true : undefined}>
        {edit &&
          !taskObject.completed &&
          (authorizedUser.hcaid === taskObject.userId || isEnterpriseAdmin) && (
            <DeleteBtn modalContentRef={modalContentRef} task={taskObject} />
          )}
        {!edit && (
          <NeuButton
            data-testid="add-issue-modal-save-button"
            disabled={!showButton ? true : undefined}
            color="blue"
            size="small"
            onClick={() => {
              if (modalAction === 'UNIVERSAL-ADD') {
                logTrackingEvent(logAdHocClick(modalType, 'save'));
              } else {
                logTrackingEvent(logTaskClick('issues', 'save'));
              }
              if (haveControlNumber === true && taskObject.u_control_number) {
                dispatch(verifyControlNumber(taskObject.u_control_number));
              } else {
                handlePost();
              }
            }}
          >
            {loadingSaveTask || isLoadingControlNumber ? (
              <BtnSpinner
                aria-label="spinner"
                className="w-100 mx-0"
                kind="spinner"
                role="status"
                size="small"
              />
            ) : (
              'Save Issue'
            )}
          </NeuButton>
        )}
        {edit && !taskObject.completed && (
          <NeuButton
            data-testid="edit-issue-modal-save-button"
            disabled={!showButton ? true : undefined}
            color="blue"
            size="small"
            onClick={showButton ? handlePost : undefined}
          >
            Save Issue
          </NeuButton>
        )}
        {edit &&
          taskObject.taskDate &&
          taskObject.completed &&
          taskObject.completedDate &&
          !taskObject.spotlight && (
            <>
              <div className="d-flex flex-row align-items-center px-1">
                <StyledCircleDoneIcon color="plain">done</StyledCircleDoneIcon>
                <div className="ms-2">
                  <StyledEditHeaderRow className="ps-0">
                    <StyledBoldHeadingCompleted className="mt-0" autoWidth>
                      Completed
                    </StyledBoldHeadingCompleted>
                  </StyledEditHeaderRow>
                  <StyledEditHeaderRow className="ps-0">
                    <InputHeading
                      className="mt-0"
                      autoWidth
                      style={{ fontSize: '0.75rem' }}
                    >
                      {taskObject.completedDate &&
                        date.dayOfYr(taskObject.completedDate)}{' '}
                      @{' '}
                      {taskObject.completedDate &&
                        date.localTimeOfDay(taskObject.completedDate)}
                    </InputHeading>
                  </StyledEditHeaderRow>
                </div>
              </div>
              <div>
                {isEnterpriseAdmin && (
                  <DeleteBtn
                    modalContentRef={modalContentRef}
                    task={taskObject}
                  />
                )}
              </div>
            </>
          )}
      </ModalFooter>
    </AddIssueCard>
  );
};

const mapReduxStateToProps = (state: RootState) => ({
  attachedStoplight: state.StoplightReducer.stoplight,
  authorizedUser: state.AuthorizedUser.authorizedUser,
  categories: state.TaskReducer.categories,
  controlNumberResponse: state.TaskReducer.controlNumberResponse,
  geLocations: state.TaskReducer.geLocations,
  isLoadingControlNumber: state.TaskReducer.isLoadingControlNumber,
  isRoundStarted: state.RoundReducer.isRoundStarted,
  issueData: state.ModalReducer.modalData,
  loadingSaveTask: state.TaskReducer.loadingPostNewTask,
  modalAction: state.ModalReducer.modalAction,
  modalType: state.ModalReducer.modalType,
  scLocations: state.TaskReducer.scLocations,
  stoplight: state.StoplightReducer.stoplight,
  tempModalState: state.TaskReducer.tempModalState,
  tempTaskObject: state.TaskReducer.tempTaskObject,
  tempShareWithEmailObject: state.TaskReducer.tempShareWithEmailObject,
  units: state.ConfigReducer.units,
  userSection: state.UserReducer.userSection
});

export default connect(mapReduxStateToProps)(AddIssueModal);
